import { useIntl, FormattedMessage } from 'react-intl';
import { Formik, Form } from 'formik';
import {
    Button,
    Box,
    Heading,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
} from '@chakra-ui/react';
import styled from '@emotion/styled/macro';

import useCommonToast from '../../hooks/useCommonToast';

import AutoformItemFields from './AutoformItemFields';
import useAutoformTableMetadata from './hooks/useAutoformTableMetadata';
import { getDefaultValueForColumnType } from './utils/autoformUtils';

const AutoformAddSubItemModal = ({ tableId, schemaCode, itemType, onCreateRecord, ...rest }) => {
    const intl = useIntl();
    const toast = useCommonToast();
    const { sortedColumns } = useAutoformTableMetadata(tableId);

    const initialFormValues =
        (sortedColumns &&
            sortedColumns.reduce((acc, col) => {
                acc[col.name] = getDefaultValueForColumnType(col.dataTypeDescription);
                return acc;
            }, {})) ||
        {};

    const onFormSubmit = async (payload) => {
        const errorMessage = intl.formatMessage({ id: 'common_generic_saving_error' });

        try {
            await onCreateRecord(payload);

            rest.onClose && rest.onClose();
        } catch (error) {
            if (error.response.status !== 401) {
                toast({
                    status: 'error',
                    message: errorMessage,
                });
            }
        }
    };

    return (
        <Modal {...rest}>
            <ModalOverlay />

            <StyledModal pt={3}>
                <ModalHeader pr={12}>
                    <Heading as="h3" variant="h3" textTransform="capitalize">
                        <FormattedMessage id="common_add_subitem_modal_heading" values={{ item: itemType }} />
                    </Heading>
                </ModalHeader>
                <ModalCloseButton mt={4} mr={3} h={6} w={6} />

                <Formik initialValues={initialFormValues} onSubmit={onFormSubmit}>
                    {({ isSubmitting, isValid, dirty }) => (
                        <Form>
                            <ModalBody borderBottom="1px" borderColor="gray.200">
                                <AutoformItemFields
                                    tableId={tableId}
                                    skipFirstColumn={false}
                                    getInputFormName={(_, __, columnName) => columnName}
                                    mode="add"
                                />
                            </ModalBody>

                            <ModalFooter justifyContent="stretch" flexWrap="wrap">
                                <Box ml="auto">
                                    <Button variant="secondary" onClick={rest.onClose}>
                                        <Box as="span" textTransform="capitalize">
                                            <FormattedMessage id="common_cancel" />
                                        </Box>
                                    </Button>

                                    <Button
                                        type="submit"
                                        isDisabled={!isValid || !dirty}
                                        isLoading={isSubmitting}
                                        ml={3}
                                    >
                                        <FormattedMessage id="common_item_create_btn" />
                                    </Button>
                                </Box>
                            </ModalFooter>
                        </Form>
                    )}
                </Formik>
            </StyledModal>
        </Modal>
    );
};

const StyledModal = styled(ModalContent)`
    margin: auto;
`;

export default AutoformAddSubItemModal;
